// Iter109 — Market Scanner → Strategy Scanner bridge context.
//
// Hard contract:
//   * Frontend-only.  No backend call, no new endpoint, no
//     persistence (no localStorage / sessionStorage / cookies).
//   * Pure in-memory state: when the Market Scanner completes a
//     successful pass it publishes `{ symbols, completedAt,
//     opportunities, universe }` here; the Strategy Scanner reads
//     it on demand (Pull link + Auto-chip).
//   * Lost on full page reload — by design.
//   * Read-only contract: the bridge propagates SYMBOLS ONLY.
//     No ranking, no winner, no auto-select, no order, no
//     journal mutation.  The Strategy Scanner still requires the
//     operator to click "Run Unified Strategy Scan".
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const _Ctx = createContext(null);

export function MarketScannerBridgeProvider({ children }) {
  // The single bridge payload.  `null` means no Market Scanner
  // pass has completed yet in this session.
  const [pass, setPass] = useState(null);

  // Publish the latest scanner pass.  Called by the Market
  // Scanner page after a successful results fetch.
  const publish = useCallback((payload) => {
    if (!payload || !Array.isArray(payload.symbols)) return;
    const cleanSymbols = payload.symbols
      .map(s => String(s || "").trim().toUpperCase())
      .filter(s => s.length > 0)
      .filter((s, i, a) => a.indexOf(s) === i);
    if (cleanSymbols.length === 0) return;
    setPass({
      symbols: cleanSymbols,
      completedAt: payload.completedAt || new Date().toISOString(),
      opportunities: payload.opportunities ?? cleanSymbols.length,
      universe: payload.universe ?? null,
      scanId: payload.scanId || null,
    });
  }, []);

  // Operator dismisses the auto-chip (does NOT clear the pull
  // link — the chip is just the proactive surface).
  const dismiss = useCallback(() => {
    setPass(prev => prev ? { ...prev, _dismissed: true } : prev);
  }, []);

  // Hard clear (Pull-link consumes its read).
  const clear = useCallback(() => setPass(null), []);

  const value = useMemo(() => ({
    pass, publish, dismiss, clear,
  }), [pass, publish, dismiss, clear]);

  return <_Ctx.Provider value={value}>{children}</_Ctx.Provider>;
}

export function useMarketScannerBridge() {
  const v = useContext(_Ctx);
  if (!v) {
    // Defensive default — keeps the page from crashing if the
    // provider is unmounted in tests.
    return {
      pass: null,
      publish: () => {},
      dismiss: () => {},
      clear: () => {},
    };
  }
  return v;
}

export default MarketScannerBridgeProvider;
